/**
 * The wording and the client-side rules for charging one card against many
 * invoices (#1345).
 *
 * React-free so it can be tested without mounting anything — the same reason
 * `customers/batch-actions.ts` is. The server owns the real arithmetic (it
 * re-reads every balance inside the transaction and never trusts a total the
 * browser sends); this is what the operator READS before they commit, and
 * wrong wording on a money screen is its own kind of bug.
 */

/** Mirrors the server's `MAX_BATCH_INVOICES`. Refuses past it; never truncates. */
export const MAX_BATCH_INVOICES = 50;

export interface InvoiceLike {
    id: string;
    status: string;
    balance?: string | null;
    total_amount: string;
    amount_paid: string;
}

export function centsOf(dollars: string | number | null | undefined): number {
    if (dollars === null || dollars === undefined || dollars === "") return 0;
    const n = typeof dollars === "number" ? dollars : Number(dollars);
    if (!Number.isFinite(n)) return 0;
    return Math.round(n * 100);
}

export function money(cents: number): string {
    const sign = cents < 0 ? "-" : "";
    return `${sign}$${(Math.abs(cents) / 100).toLocaleString("en-US", {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
    })}`;
}

/** What's left on an invoice, preferring the server's computed balance. */
export function balanceCents(r: InvoiceLike): number {
    if (r.balance !== null && r.balance !== undefined && r.balance !== "") return centsOf(r.balance);
    return centsOf(r.total_amount) - centsOf(r.amount_paid);
}

/**
 * Can this row go in a batch?
 *
 * Deliberately permissive about *why* — the server re-checks and is the
 * authority. This exists so a paid row's checkbox is visibly disabled rather
 * than accepting a click the charge would then refuse.
 */
export function isChargeable(r: InvoiceLike): boolean {
    if (r.status !== "open" && r.status !== "partially_paid") return false;
    return balanceCents(r) > 0;
}

/** Why the checkbox is disabled, in the office's words. */
export function unselectableReason(r: InvoiceLike): string {
    if (r.status === "paid") return "Already paid — nothing to charge.";
    if (r.status === "void") return "This invoice is void.";
    if (r.status !== "open" && r.status !== "partially_paid") return `This invoice is ${r.status.replace(/_/g, " ")}.`;
    return "Nothing is due on this invoice.";
}

/**
 * What the header checkbox actually covers, said out loud.
 *
 * The Invoices tab is server-paginated over accounts with thousands of rows
 * (Cintas: 3,030 invoices, 101 unpaid). "Select all" that silently means "the
 * 50 on screen" is the bug; so is one that means all 101 when only 50 are
 * loaded. Either way the operator is told which it is.
 */
export function selectionScopeLabel(loaded: number, total: number | undefined, chargeableOnPage: number): string {
    const pickable = `${chargeableOnPage} of the ${loaded} invoice${loaded === 1 ? "" : "s"} loaded below`;
    if (total === undefined || total <= loaded) {
        return `Selects ${pickable} — the rest are paid, void or have nothing due.`;
    }
    return `Selects ${pickable}, not all ${total} that match this filter. Load more, or narrow the filter first.`;
}

export type BatchSelectionError = { code: "too_many"; message: string } | null;

/** Refuses past the cap rather than charging the first 50. */
export function validateBatchSelection(count: number): BatchSelectionError {
    if (count > MAX_BATCH_INVOICES) {
        return {
            code: "too_many",
            message:
                `${count} invoices selected. One batch is capped at ${MAX_BATCH_INVOICES} so a ` +
                `mis-click can't become a five-figure charge. Narrow the filter, or work through ` +
                `it in batches.`,
        };
    }
    return null;
}

/** The label on the selection bar's button, with the money in it. */
export function chargeButtonLabel(totalCents: number): string {
    return totalCents > 0 ? `Charge ${money(totalCents)} to a card…` : "Charge a card…";
}

/** How a card reads in the picker: the name first, the digits second. */
export function cardLabel(card: { label?: string | null; brand?: string | null; last4?: string | null }): string {
    const digits = card.last4 ? `•••• ${card.last4}` : "card";
    const brand = card.brand ? titleCase(card.brand) : "";
    const right = [brand, digits].filter(Boolean).join(" ");
    return card.label ? `${card.label} — ${right}` : right;
}

function titleCase(s: string): string {
    return s.charAt(0).toUpperCase() + s.slice(1);
}

/** Expiry, or "" when Stripe didn't give us one. */
export function cardExpiry(card: { exp_month?: number | null; exp_year?: number | null }): string {
    if (!card.exp_month || !card.exp_year) return "";
    const yy = String(card.exp_year).slice(-2);
    return `${String(card.exp_month).padStart(2, "0")}/${yy}`;
}

/**
 * The result line.
 *
 * One transaction, so there is no half-state: it either settled every invoice
 * or none. The declined wording says the second part out loud — "declined"
 * alone leaves the operator wondering what happened to the invoices.
 */
export function batchResultLine(input: {
    charged: boolean;
    totalCents: number;
    invoiceCount: number;
    reason?: string | null;
}): string {
    const invoices = `${input.invoiceCount} invoice${input.invoiceCount === 1 ? "" : "s"}`;
    if (input.charged) return `${money(input.totalCents)} charged. ${invoices} paid.`;
    // Plain reasons already end in a period ("Not enough money on the card.").
    const why = input.reason?.trim().replace(/\.+$/, "");
    return (
        `Nothing was charged and no invoice was marked paid.` +
        (why ? ` The card said: ${why}.` : "") +
        ` All ${invoices} are exactly as they were.`
    );
}

/** Mirrors the server's typed-confirm check so the button can gate on it. */
export function typedConfirmMatches(typed: string, totalCents: number): boolean {
    const cleaned = typed.replace(/[$,\s]/g, "");
    if (!/^\d+(\.\d{1,2})?$/.test(cleaned)) return false;
    return centsOf(cleaned) === totalCents;
}
